/**
 * API service — calls Raapid backend for coding suggestions.
 *
 * In mock mode, loads data from the local dummy-data files (served by
 * mock-api or imported as JSON).  In live mode, calls the real API with
 * the SMART access token in the Authorization header.
 */
import { generateUUID } from '../utils/uuid';
import type {
  CodingSuggestionsResponse,
  AcceptPayload,
  RejectPayload,
  CodingSuggestion,
} from '../types';

// Vite injects VITE_ env vars at build time
const API_BASE = import.meta.env.VITE_API_BASE ?? '/raapid-api';
const IS_MOCK = import.meta.env.VITE_MOCK_MODE === 'true';

// ─── Idempotency key store (in-memory, session-scoped) ────────────────────────
// Prevents duplicate writes if the network call retries.
const idempotencyKeys = new Map<string, string>();

function getIdempotencyKey(suggestionId: string): string {
  if (!idempotencyKeys.has(suggestionId)) {
    idempotencyKeys.set(suggestionId, generateUUID());
  }
  return idempotencyKeys.get(suggestionId)!;
}

// ─── Auth header helper ───────────────────────────────────────────────────────

function authHeaders(accessToken?: string | null): HeadersInit {
  const base: HeadersInit = { 'Content-Type': 'application/json' };
  if (accessToken) {
    return { ...base, Authorization: `Bearer ${accessToken}` };
  }
  return base;
}

// ─── Fetch with error handling, timeout, and abort support ───────────────────

const DEFAULT_TIMEOUT_MS = 15_000; // 15s — EHR networks can be slow

async function apiFetch<T>(
  path: string,
  options?: RequestInit & { timeoutMs?: number },
  accessToken?: string | null,
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options ?? {};

  // AbortController for both timeout and external cancellation
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), timeoutMs);

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        ...authHeaders(accessToken),
        ...(fetchOptions.headers ?? {}),
      },
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`API ${res.status}: ${body || res.statusText}`);
    }

    return res.json() as Promise<T>;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Request timed out. Check your network connection and try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

// ─── GET /api/v1/results/{patientId} ─────────────────────────────────────────

export async function getCodingSuggestions(
  patientId: string,
  accessToken?: string | null,
): Promise<CodingSuggestionsResponse> {
  if (IS_MOCK) {
    // Load from mock API (which serves the dummy-data JSON)
    return apiFetch<CodingSuggestionsResponse>(
      `/v1/results/${patientId}`,
    );
  }
  return apiFetch<CodingSuggestionsResponse>(
    `/v1/results/${patientId}`,
    undefined,
    accessToken,
  );
}

// ─── POST /api/v1/results/{patientId}/suggestions/{suggestionId}/accept ───────

export async function acceptSuggestion(
  patientId: string,
  payload: AcceptPayload,
  accessToken?: string | null,
): Promise<CodingSuggestion> {
  const { suggestionId } = payload;
  return apiFetch<CodingSuggestion>(
    `/v1/results/${patientId}/suggestions/${suggestionId}/accept`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        // Idempotency: safe to retry on network failure
        'Idempotency-Key': getIdempotencyKey(`accept-${suggestionId}`),
      },
    },
    accessToken,
  );
}

// ─── POST /api/v1/results/{patientId}/suggestions/{suggestionId}/reject ───────

export async function rejectSuggestion(
  patientId: string,
  payload: RejectPayload,
  accessToken?: string | null,
): Promise<CodingSuggestion> {
  const { suggestionId } = payload;
  return apiFetch<CodingSuggestion>(
    `/v1/results/${patientId}/suggestions/${suggestionId}/reject`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Idempotency-Key': getIdempotencyKey(`reject-${suggestionId}`),
      },
    },
    accessToken,
  );
}

// ─── GET /api/v1/patients/{patientId}/audit-trail ────────────────────────────

export async function getAuditTrail(
  patientId: string,
  accessToken?: string | null,
) {
  return apiFetch<unknown[]>(
    `/v1/patients/${patientId}/audit-trail`,
    undefined,
    accessToken,
  );
}
